import { AxiosError, AxiosResponse } from 'axios';
import { DefaultTFuncReturn } from 'i18next';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { usePostLogsDownload } from '../../../api/logs/logs';
import { ErrorInfo, LogSearch } from '../../../api/model';
import { useDefaultOptions, useOptionsForBinary } from '../../../hooks/api';
import { executeDownload } from '../../../utils/download';
import * as sessionStorageUtils from '../../../utils/sessionStorage';
import ErrorComponent from '../../atoms/ErrorComponent';
import NoDataComponent from '../../atoms/NoDataComponent';
import WaitComponent from '../../atoms/WaitComponent';
import BlobErrorModal from '../../modules/BlobErrorModal';
import PageNavi from '../../modules/PageNavi';
import SpinnerModal from '../../modules/SpinnerModal';

interface Props {
  data: AxiosResponse<LogSearch, unknown> | undefined;
  isLoading: boolean;
  isError: boolean;
  error: AxiosError<ErrorInfo> | null;
}

const N_DATA_PER_PAGE = 20;

// 一覧のソートキー
type SortKey = 'log_date' | 'log_path';

interface SortableThProps {
  label: DefaultTFuncReturn;
  onClick: () => void;
}

function SortableTh({ label, onClick }: SortableThProps) {
  return (
    <th>
      <button type="button" className="text-button" onClick={onClick}>
        {label}
        <i className="fa-solid fa-sort" />
      </button>
    </th>
  );
}

function LogList({ data, isLoading, isError, error }: Props) {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const options = useDefaultOptions(language);

  // 現在表示中のページ番号
  const [currentPage, setCurrentPage] = useState(sessionStorageUtils.get('Logs.CurrentPage', 1));

  // ソート情報
  const [sortInfo, setSortInfo] = useState(
    sessionStorageUtils.get('Logs.SortInfo', { key: 'log_date' as SortKey, isDisc: false })
  );

  // 初回ロード中かどうか
  const refIsFirstLoading = useRef(true);

  // 一覧データをソートしたもの
  const sortedLogs = useMemo(
    () =>
      [...(data?.data.log_folders ?? [])].sort((a, b) => {
        const aAny = a as { [key: string]: string | undefined };
        const bAny = b as { [key: string]: string | undefined };
        const multiValue = sortInfo.isDisc ? 1 : -1;
        if (aAny[sortInfo.key] !== bAny[sortInfo.key]) {
          // 第一ソートキー
          return ((aAny[sortInfo.key] ?? '') > (bAny[sortInfo.key] ?? '') ? -1 : 1) * multiValue;
        }
        return 1;
      }),
    [sortInfo, data]
  );
  // 現在のページに表示する一覧
  const logsForPresentPage = useMemo(
    () => sortedLogs.slice(N_DATA_PER_PAGE * (currentPage - 1), N_DATA_PER_PAGE * currentPage),
    [currentPage, sortedLogs]
  );

  // 一覧のソートキーを変更する関数
  const changeSortKey = useCallback((key: SortKey) => {
    setSortInfo((pre) => {
      const next = { key, isDisc: pre.key === key && !pre.isDisc };
      sessionStorageUtils.set('Logs.SortInfo', next);
      return next;
    });
    setCurrentPage(1);
  }, []);

  // 現在ページを変更する関数
  const changeCurrentPage = useCallback(
    (next: number) => {
      sessionStorageUtils.set('Logs.CurrentPage', next);
      setCurrentPage(next);
    },
    [setCurrentPage]
  );

  // データが変更されたときの動作
  useEffect(() => {
    if (data?.data.log_folders != null) {
      if (refIsFirstLoading.current) {
        // 初回ロード完了時は初回ロードが完了したことを設定する
        refIsFirstLoading.current = false;
      } else {
        // 初回ロード以外にデータが変更されたときは、現在ページを 1 ページにする
        setCurrentPage(1);
      }
    }
  }, [data?.data.log_folders]);

  /* ダウンロード */
  const [displayDownloadError, setDisplayDownloadError] = useState(false);
  const optionsForBinary = useOptionsForBinary(language);
  const {
    mutateAsync: _downloadAsync,
    isLoading: downloiadLoading,
    isError: downloadIsError,
    error: downloadError,
  } = usePostLogsDownload<AxiosError<Blob>>(optionsForBinary);
  useEffect(() => {
    setDisplayDownloadError(downloadIsError);
  }, [downloadIsError]);

  // ダウンロード処理
  const downloadAsync = useCallback(
    async (logDate: string | undefined) => {
      const response = await _downloadAsync({ data: { folder_name: logDate } });
      if (response.status === 200) {
        executeDownload(response);
      }
    },
    [_downloadAsync]
  );

  // データ取得中
  if (isLoading) {
    return <WaitComponent />;
  }

  // エラー表示
  if (isError) {
    return <ErrorComponent error={error} />;
  }

  // データが空の場合
  if (data == null || data.data.log_folders == null) {
    return <NoDataComponent />;
  }

  return (
    <div>
      <div style={{ height: '40px' }}>
        <h3 className="trig">
          <span>{t('ログ一覧')}</span>
        </h3>
      </div>
      <div className="wfr in-pgnavi">
        <table className="table40 mb30">
          <tbody>
            <tr>
              <SortableTh label={t('ログ日付')} onClick={() => changeSortKey('log_date')} />
              <SortableTh label={t('フォルダパス')} onClick={() => changeSortKey('log_path')} />
              <th style={{ width: '50px' }}>{t('ダウンロード')}</th>
            </tr>
            {logsForPresentPage.map((x) => (
              <tr key={x.log_date}>
                <td style={{ width: '30%' }}>{x.log_date}</td>
                <td style={{ width: '60%' }}>{x.log_path}</td>
                <td>
                  <button
                    type="button"
                    className="text-button"
                    id={x.log_date}
                    onClick={() => downloadAsync(x.log_date)}
                  >
                    <i className="fa-solid fa-download" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <PageNavi
          current={currentPage}
          pageCount={Math.ceil(data.data.log_folders.length / (N_DATA_PER_PAGE * 1.0))}
          onChangePage={changeCurrentPage}
        />
      </div>
      {/* スピナーモーダル */}
      {downloiadLoading && <SpinnerModal />}
      {/* 出力エラー */}
      {displayDownloadError && (
        <BlobErrorModal error={downloadError} onClickOk={() => setDisplayDownloadError(false)} />
      )}
    </div>
  );
}
export { LogList };
